import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getHuman,
  evaluateFace,
  guidanceMessage,
  estimateLighting,
  captureFrame,
} from '../services/faceEngine';
import './FaceCaptureModal.css';

// Instruções de pose para o cadastro das 3 fotos de referência.
const ENROLL_STEPS = [
  'Olhe de frente para a câmera',
  'Vire LEVEMENTE o rosto para a ESQUERDA',
  'Vire LEVEMENTE o rosto para a DIREITA',
];

// Quantos ciclos seguidos de qualidade OK antes da captura automática
// (~150ms por ciclo → ~0,6s de rosto estável).
const STABLE_CYCLES = 4;

/**
 * Modal de câmera para reconhecimento facial.
 *
 * mode='enroll'  → captura 3 fotos guiadas e chama onComplete([{blob, dataUrl, descriptor} x3]).
 * mode='verify'  → captura 1 foto e chama onComplete({blob, dataUrl, descriptor}).
 *
 * props:
 *  - personName: nome exibido no cabeçalho
 *  - allowSkip / onSkip: no modo verify opcional, permite registrar sem verificação
 *  - busyText: texto externo de processamento (ex.: "Verificando..." enquanto o backend responde)
 *  - resultBanner: { type: 'success'|'error', text } exibido sobre o vídeo (resultado da verificação)
 *  - onRetry: habilita botão "Tentar novamente" quando resultBanner é de erro
 *  - chooseCamera: abre num passo de escolha da câmera (com prévia) e só
 *    começa a detectar/capturar depois do "Iniciar captura". Usado no
 *    cadastro, onde a captura é automática e pegar a câmera errada obriga a
 *    refazer tudo.
 */

/** Última câmera escolhida (o admin cadastra várias pessoas seguidas). */
const CAMERA_STORAGE_KEY = 'faceCameraDeviceId';

function FaceCaptureModal({
  mode,
  personName,
  onComplete,
  onClose,
  allowSkip = false,
  onSkip,
  busyText = null,
  resultBanner = null,
  onRetry = null,
  initialFacing = 'user', // 'user' = dianteira/frontal, 'environment' = traseira
  onConfirm = null, // quando definido, o sucesso mostra um botão em vez de registrar sozinho
  confirmLabel = 'Confirmar',
  chooseCamera = false,
}) {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef({ running: false, stable: 0, paused: false, pauseUntil: 0 });

  // loading | choose | camera-error | engine-error | detecting
  const [phase, setPhase] = useState('loading');
  const [errorDetail, setErrorDetail] = useState('');
  const [guidance, setGuidance] = useState('Carregando modelos de reconhecimento...');
  const [checksOk, setChecksOk] = useState(false);
  const [checks, setChecks] = useState(null); // checklist ao vivo de qualidade
  const [liveness, setLiveness] = useState(null); // sinal advisory de vivacidade
  const [captures, setCaptures] = useState([]); // enroll: fotos já tiradas
  const [flash, setFlash] = useState(false);
  const [facing, setFacing] = useState(initialFacing); // dianteira/traseira

  // Escolha de câmera: `deviceId` é a escolha explícita (vazio = deixa o
  // navegador decidir pelo facingMode); `activeDeviceId` é a que o stream
  // acabou usando, só para o <select> refletir a realidade sem reiniciar.
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(
    () => (chooseCamera && localStorage.getItem(CAMERA_STORAGE_KEY)) || '',
  );
  const [activeDeviceId, setActiveDeviceId] = useState('');
  const [started, setStarted] = useState(!chooseCamera);

  const capturesRef = useRef(captures);
  capturesRef.current = captures;

  // Pausa o loop de captura enquanto o chamador processa (verify no backend)
  // ou exibe resultado.
  loopRef.current.paused = Boolean(busyText || resultBanner);

  const stopStream = useCallback(() => {
    loopRef.current.running = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function openStream() {
      const size = { width: { ideal: 640 }, height: { ideal: 480 } };
      if (deviceId) {
        try {
          return await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: deviceId }, ...size },
            audio: false,
          });
        } catch (err) {
          // Câmera lembrada não existe mais (outro computador, USB tirada):
          // esquece a escolha e cai no padrão em vez de travar a tela.
          if (err?.name === 'OverconstrainedError' || err?.name === 'NotFoundError') {
            localStorage.removeItem(CAMERA_STORAGE_KEY);
            setDeviceId('');
          } else {
            throw err;
          }
        }
      }
      // facingMode como preferência (não `exact`): em desktop com só uma
      // webcam não falha; em celular/tablet escolhe frontal/traseira.
      return navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, ...size },
        audio: false,
      });
    }

    async function start() {
      // 1. câmera
      let stream;
      try {
        stream = await openStream();
      } catch (err) {
        if (!cancelled) {
          setPhase('camera-error');
          setErrorDetail(
            err?.name === 'NotAllowedError'
              ? 'Permissão de câmera negada. Libere a câmera para este site e tente de novo.'
              : `Não foi possível abrir a câmera (${err?.name || 'erro'}). Verifique se outro programa não está usando a câmera.`,
          );
        }
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (e) {
          /* autoplay já resolvido pelo atributo */
        }
      }

      // 1b. lista de câmeras — só depois do getUserMedia os rótulos vêm
      // preenchidos (antes da permissão o navegador devolve label vazio).
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const cams = all.filter((d) => d.kind === 'videoinput');
        const inUse = stream.getVideoTracks()[0]?.getSettings?.().deviceId || '';
        if (!cancelled) {
          setDevices(cams);
          setActiveDeviceId(inUse);
        }
      } catch (e) {
        /* sem lista: a tela cai no botão de frontal/traseira */
      }
      if (cancelled) return;

      // 1c. no cadastro, para aqui: o operador confere a prévia, escolhe a
      // câmera e só então libera a captura (que é automática).
      if (!started) {
        setPhase('choose');
        return;
      }

      // 2. modelos
      let human;
      try {
        human = await getHuman();
      } catch (err) {
        if (!cancelled) {
          setPhase('engine-error');
          setErrorDetail(
            'Falha ao carregar os modelos de reconhecimento facial. ' +
              'Verifique a conexão e recarregue a página.',
          );
        }
        return;
      }
      if (cancelled) return;

      setPhase('detecting');
      loopRef.current.running = true;
      detectLoop(human);
    }

    async function detectLoop(human) {
      while (loopRef.current.running) {
        const video = videoRef.current;
        if (
          !video ||
          video.readyState < 2 ||
          loopRef.current.paused ||
          Date.now() < loopRef.current.pauseUntil
        ) {
          loopRef.current.stable = 0;
          await new Promise((r) => setTimeout(r, 150));
          continue;
        }
        let result;
        try {
          result = await human.detect(video);
        } catch (e) {
          await new Promise((r) => setTimeout(r, 300));
          continue;
        }
        if (!loopRef.current.running) break;

        const rawBox = result?.face?.[0]?.box;
        const lighting = estimateLighting(video, rawBox);
        const { face, checks: liveChecks, liveness, lightHint, allOk } = evaluateFace(
          result,
          video.videoWidth,
          video.videoHeight,
          lighting,
        );
        drawOverlay(face, allOk);
        setChecksOk(allOk);
        setChecks(liveChecks);
        setLiveness(liveness);
        setGuidance(guidanceMessage(liveChecks, lightHint));

        if (allOk) {
          loopRef.current.stable += 1;
          if (loopRef.current.stable >= STABLE_CYCLES) {
            loopRef.current.stable = 0;
            await takeCapture(face, liveness);
          }
        } else {
          loopRef.current.stable = 0;
        }
        await new Promise((r) => setTimeout(r, 150));
      }
    }

    async function takeCapture(face, liveness) {
      const video = videoRef.current;
      if (!video) return;
      const { blob, dataUrl } = await captureFrame(video);
      const capture = {
        blob,
        dataUrl,
        descriptor: Array.from(face.embedding),
        // Sinal advisory de vivacidade para o chamador avisar (não bloqueia).
        livenessSuspicious: !!liveness?.suspicious,
      };

      setFlash(true);
      setTimeout(() => setFlash(false), 250);

      if (mode === 'verify') {
        loopRef.current.paused = true;
        onComplete(capture);
        return;
      }

      // enroll: acumula até 3
      const next = [...capturesRef.current, capture];
      setCaptures(next);
      if (next.length >= ENROLL_STEPS.length) {
        loopRef.current.paused = true;
        onComplete(next);
      } else {
        // Pequena pausa entre fotos para a pessoa mudar de pose.
        loopRef.current.pauseUntil = Date.now() + 1200;
      }
    }

    function drawOverlay(face, ok) {
      const canvas = overlayRef.current;
      const video = videoRef.current;
      if (!canvas || !video || !video.videoWidth) return;
      if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth;
      if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (face?.box) {
        const [x, y, w, h] = face.box;
        ctx.strokeStyle = ok ? '#00C853' : '#FFB300';
        ctx.lineWidth = 3;
        ctx.strokeRect(x, y, w, h);
      }
    }

    setPhase('loading');
    start();
    return () => {
      cancelled = true;
      stopStream();
    };
    // Reinicia a câmera quando trocam frontal<->traseira, escolhem outro
    // dispositivo na lista ou liberam o início da captura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing, deviceId, started]);

  const handleClose = () => {
    stopStream();
    onClose();
  };

  const handleSkip = () => {
    stopStream();
    if (onSkip) onSkip();
  };

  const toggleFacing = () => {
    // Trocar de lado invalida a escolha por dispositivo (são constraints
    // concorrentes: com deviceId `exact` o facingMode é ignorado).
    setDeviceId('');
    localStorage.removeItem(CAMERA_STORAGE_KEY);
    setFacing((f) => (f === 'user' ? 'environment' : 'user'));
  };

  const handlePickDevice = (id) => {
    setDeviceId(id);
    if (id) localStorage.setItem(CAMERA_STORAGE_KEY, id);
    else localStorage.removeItem(CAMERA_STORAGE_KEY);
  };

  const enrollStep = Math.min(captures.length, ENROLL_STEPS.length - 1);
  const title =
    mode === 'enroll'
      ? `Cadastrar rosto — ${personName}`
      : `Verificação facial — ${personName}`;

  // Checklist ao vivo do ENQUADRAMENTO (o que dispara a captura).
  const checklist = checks
    ? [
        { label: 'Rosto detectado', ok: checks.oneFace },
        {
          label: 'Perto o suficiente',
          ok: checks.notTooFar,
        },
        {
          label: 'Centralizado e de frente',
          ok: checks.centered && checks.frontal,
        },
        {
          label: 'Boa iluminação',
          ok: checks.wellLit !== false && checks.faceScore,
        },
      ]
    : [];
  const showLiveUi = phase === 'detecting' && !busyText && !resultBanner;

  return (
    <div className="fc-overlay" onClick={handleClose}>
      <div
        className={`fc-modal ${facing === 'user' ? 'fc-facing-user' : 'fc-facing-env'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fc-header">
          <h2>{title}</h2>
          <button type="button" className="fc-close" onClick={handleClose} aria-label="Fechar">
            ×
          </button>
        </div>

        <div className="fc-body">
          {phase === 'camera-error' || phase === 'engine-error' ? (
            <div className="fc-error-box">
              <span className="fc-error-icon">📷</span>
              <p>{errorDetail}</p>
            </div>
          ) : (
            <>
              <div className="fc-video-wrap">
                <video ref={videoRef} className="fc-video" autoPlay playsInline muted />
                <canvas ref={overlayRef} className="fc-overlay-canvas" />
                {/* Círculo-guia de enquadramento: fica verde quando o rosto
                    está pegando bem. */}
                {showLiveUi && (
                  <div className={`fc-frame ${checksOk ? 'fc-frame--ok' : ''}`} />
                )}
                {/* Pílula de status grande sobre o vídeo. */}
                {showLiveUi && (
                  <div className={`fc-pill ${checksOk ? 'fc-pill--ok' : ''}`}>
                    {checksOk ? '✓ ' : ''}
                    {guidance}
                  </div>
                )}
                {flash && <div className="fc-flash" />}
                {phase === 'loading' && (
                  <div className="fc-video-status">
                    <div className="fc-spinner" />
                    <span>Carregando modelos de reconhecimento...</span>
                  </div>
                )}
                {busyText && (
                  <div className="fc-video-status">
                    <div className="fc-spinner" />
                    <span>{busyText}</span>
                  </div>
                )}
                {resultBanner && (
                  <div className={`fc-result fc-result--${resultBanner.type}`}>
                    <span className="fc-result-icon">
                      {resultBanner.type === 'success' ? '✔' : '✖'}
                    </span>
                    <span>{resultBanner.text}</span>
                  </div>
                )}
              </div>

              {phase === 'choose' && (
                <div className="fc-choose">
                  <label className="fc-choose-label" htmlFor="fc-device-select">
                    Câmera que será usada
                  </label>
                  {devices.length > 1 ? (
                    <select
                      id="fc-device-select"
                      className="fc-choose-select"
                      value={deviceId || activeDeviceId}
                      onChange={(e) => handlePickDevice(e.target.value)}
                    >
                      {devices.map((device, i) => (
                        <option key={device.deviceId || i} value={device.deviceId}>
                          {device.label || `Câmera ${i + 1}`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="fc-choose-single">
                      {devices[0]?.label || 'Câmera padrão do dispositivo'}
                      {' '}(única disponível)
                    </p>
                  )}
                  <p className="fc-choose-hint">
                    Confira a imagem acima. Ao iniciar, as 3 fotos são tiradas
                    <strong> automaticamente</strong> conforme o rosto for
                    posicionado.
                  </p>
                </div>
              )}

              {showLiveUi && (
                <>
                  {mode === 'enroll' && (
                    <div className="fc-step">
                      Foto {captures.length + 1} de {ENROLL_STEPS.length}:{' '}
                      <strong>{ENROLL_STEPS[enrollStep]}</strong>
                    </div>
                  )}
                  {/* Checklist ao vivo: mostra o que ainda falta pra pegar bem. */}
                  <ul className="fc-checklist">
                    {checklist.map((c) => (
                      <li key={c.label} className={c.ok ? 'fc-check--ok' : 'fc-check--pending'}>
                        <span className="fc-check-icon">{c.ok ? '✓' : '○'}</span>
                        {c.label}
                      </li>
                    ))}
                  </ul>
                  {/* Vivacidade é só um AVISO (não impede a captura). */}
                  {liveness?.suspicious && (
                    <div className="fc-liveness-warn">
                      ⚠ Parece foto ou tela — confira se é a pessoa de verdade.
                    </div>
                  )}
                  <div className="fc-guidance-hint">
                    Encaixe o rosto no círculo. A captura é automática quando ele estiver bem posicionado.
                  </div>
                </>
              )}

              {mode === 'enroll' && phase !== 'choose' && (
                <div className="fc-thumbs">
                  {ENROLL_STEPS.map((_, i) => (
                    <div key={i} className={`fc-thumb ${captures[i] ? 'fc-thumb--done' : ''}`}>
                      {captures[i] ? (
                        <img src={captures[i].dataUrl} alt={`Foto ${i + 1}`} />
                      ) : (
                        <span>{i + 1}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="fc-footer">
          {phase === 'choose' && (
            <button type="button" className="btn-primary" onClick={() => setStarted(true)}>
              ▶ Iniciar captura
            </button>
          )}
          {/* Sucesso: o registro NÃO é automático — o fiscal confirma aqui. */}
          {resultBanner?.type === 'success' && onConfirm && (
            <button type="button" className="btn-register-entry fc-confirm-btn" onClick={onConfirm}>
              {confirmLabel}
            </button>
          )}
          {resultBanner?.type === 'error' && onRetry && (
            <button type="button" className="btn-primary" onClick={onRetry}>
              Tentar novamente
            </button>
          )}
          {/* Trocar entre câmera dianteira/frontal e traseira. */}
          {(phase === 'detecting' || phase === 'loading' || phase === 'choose') &&
            resultBanner?.type !== 'success' && (
            <button type="button" className="fc-flip" onClick={toggleFacing}>
              🔄 {facing === 'user' ? 'Usar câmera traseira' : 'Usar câmera frontal'}
            </button>
          )}
          {/* No modo opcional o pulo continua disponível mesmo após uma
              recusa — a decisão é do fiscal; só some quando já deu certo. */}
          {allowSkip && resultBanner?.type !== 'success' && (
            <button type="button" className="fc-skip" onClick={handleSkip}>
              Registrar sem verificação
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export default FaceCaptureModal;
